import {
    memo
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    Image,
    View
} from "react-native";
import type MemberCardProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Sticker,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../../../navigation/type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRightIcon
} from "lucide-react-native";

const MemberCard = ({
    sectors,
    item
}: MemberCardProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 992;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const getInitials = (firstName: string, lastName: string) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : "";
        const last = lastName ? lastName.charAt(0).toUpperCase() : "";
        return `${first}${last}`;
    };

    const initials = getInitials(item.firstName, item.lastName);
    const memberKey = item._id || `member-${item._id}`;

    return <TouchableOpacity
        style={[
            stylesheet.tableRow,
            {
                alignItems: isMobile ? "flex-start" : "center",
                backgroundColor: colors.project.brandWhite,
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? spaces.spacingMd : 0,
                paddingHorizontal: spaces.spacingMd,
                paddingVertical: spaces.spacingMd,
                marginBottom: spaces.spacingMd,
                borderRadius: radiuses.md
            }
        ]}
        onPress={() => {
            navigation.navigate("MemberDetail", {
                userID: item._id
            });
        }}
        activeOpacity={0.7}
    >
        <View
            style={[
                stylesheet.colUser,
                {
                    width: isMobile ? "100%" : undefined,
                    gap: spaces.spacingMd
                }
            ]}
        >
            {
                item.profilePhotoPath ?
                    <Image
                        style={[
                            stylesheet.avatarImage as never,
                            {
                                borderRadius: radiuses.full
                            }
                        ]}
                        source={{
                            uri: item.profilePhotoPath
                        }}
                        resizeMode="cover"
                    />
                :
                    <View
                        style={[
                            stylesheet.avatarPlaceholder,
                            {
                                backgroundColor: colors.project.brandBorder,
                                borderRadius: radiuses.full
                            }
                        ]}
                    >
                        <Text
                            style={{
                                color: colors.project.gold
                            }}
                            variant="titleMediumSize"
                        >
                            {initials}
                        </Text>
                    </View>
            }
            <View
                style={{
                    gap: spaces.spacingXs
                }}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {item.firstName} {item.lastName}
                </Text>
                <Text
                    style={{
                        color: colors.content.text.mid
                    }}
                    variant="bodySmallSize"
                >
                    {item.mail}
                </Text>
                <Text
                    style={{
                        color: colors.content.text.mid
                    }}
                    variant="bodySmallSize"
                >
                    {item.phoneNumber}
                </Text>
                {
                    item.isStudent ?
                        <Text
                            style={{
                                color: colors.content.text.mid
                            }}
                            variant="bodySmallSize"
                        >
                            {localize("members-student-badge")}
                        </Text>
                    :
                        null
                }
            </View>
        </View>
        <View
            style={[
                stylesheet.colContact,
                {
                    width: isMobile ? "100%" : undefined
                }
            ]}
        >
            {
                item.companyTitle ?
                    <View
                        style={[
                            stylesheet.companyContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        {
                            item.companyPhotoPath ?
                                <Image
                                    style={[
                                        stylesheet.companyLogo as never,
                                        {
                                            borderRadius: radiuses.full
                                        }
                                    ]}
                                    source={{
                                        uri: item.companyPhotoPath
                                    }}
                                    resizeMode="cover"
                                />
                            :
                                null
                        }
                        <Text
                            variant="bodyMediumSize"
                        >
                            {item.companyTitle}
                        </Text>
                    </View>
                :
                    <Text
                        variant="bodyMediumSize"
                    >
                        -
                    </Text>
            }
        </View>
        <View
            style={[
                stylesheet.colSector,
                {
                    width: isMobile ? "100%" : undefined,
                    paddingRight: spaces.spacingLg,
                    justifyContent: "flex-start",
                    gap: spaces.spacingXs,
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "wrap"
                }
            ]}
        >
            {
                item.sectorIDs && item.sectorIDs.length > 0 ?
                    item.sectorIDs.map((sId, index) => {
                        const sectorTitle = sectors.find(s => s._id === sId)?.value;
                        if (!sectorTitle) return null;
                        return <Sticker
                            key={`${memberKey}-sector-${index}`}
                            spreadBehaviour="free"
                            customColor="primary"
                            title={sectorTitle}
                            size="small"
                        />;
                    })
                :
                    <Text
                        variant="bodyMediumSize"
                    >
                        -
                    </Text>
            }
        </View>
        <View
            style={[
                stylesheet.colDate,
                {
                    width: isMobile ? "100%" : undefined
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
            >
                {item.registrationDate ? new Date(item.registrationDate).toLocaleDateString("tr-TR") : "-"}
            </Text>
        </View>
        <View
            style={[
                stylesheet.colStatus,
                {
                    width: isMobile ? "100%" : undefined
                }
            ]}
        >
            <Sticker
                title={item.isActive ? localize("members-status-active") : localize("members-status-passive")}
                type={item.isActive ? "success" : "danger"}
                spreadBehaviour="free"
                size="small"
            />
        </View>
        <View
            style={[
                stylesheet.colActions,
                {
                    display: isMobile ? "none" : "flex"
                }
            ]}
        >
            <ChevronRightIcon
                color={colors.content.icon.mid}
                size={20}
            />
        </View>
    </TouchableOpacity>;
};

export default memo(MemberCard);
